import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Download, Plus } from 'lucide-react';

export const GenerateBatchModal = ({
  isOpen,
  onClose,
  bulkMonth,
  setBulkMonth,
  bulkYear,
  setBulkYear,
  bulkMonths,
  bulkYears,
  generating,
  onGenerate
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generate Salary Batch</DialogTitle>
          <DialogDescription>
            Generate salary for all active employees for the selected month.
            Salary will be calculated from employee's existing salary structure.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-2 gap-4 py-4">
          <div>
            <Label>Month</Label>
            <Select value={String(bulkMonth)} onValueChange={(v) => setBulkMonth(Number(v))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {bulkMonths.map((m) => (
                  <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Year</Label>
            <Select value={String(bulkYear)} onValueChange={(v) => setBulkYear(Number(v))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {bulkYears.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={onGenerate} disabled={generating}>
            {generating ? (
              <>
                <div className="spinner w-4 h-4 mr-2"></div>
                Generating...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Generate Batch
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const EditBatchRecordModal = ({
  isOpen,
  onClose,
  editingRecord,
  editForm,
  setEditForm,
  onSave
}) => {
  const earningsFields = ['basic_pay', 'hra', 'da', 'special_allowance', 'conveyance', 'medical_allowance', 'other_allowances', 'overtime', 'bonus', 'arrears'];
  const deductionFields = ['pf_deduction', 'esi_deduction', 'tds_deduction', 'professional_tax', 'leave_deduction', 'loan_deduction', 'other_deductions'];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Salary Record</DialogTitle>
          <DialogDescription>
            {editingRecord?.employee_name} ({editingRecord?.employee_code})
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-2 gap-4 py-4">
          <div className="col-span-2">
            <h4 className="font-semibold text-slate-700 mb-2">Earnings</h4>
          </div>
          {earningsFields.map((field) => (
            <div key={field}>
              <Label className="capitalize">{field.replace(/_/g, ' ')}</Label>
              <Input
                type="number"
                value={editForm[field] || 0}
                onChange={(e) => setEditForm({ ...editForm, [field]: parseFloat(e.target.value) || 0 })}
              />
            </div>
          ))}
          
          <div className="col-span-2">
            <h4 className="font-semibold text-slate-700 mb-2 mt-4">Deductions</h4>
          </div>
          {deductionFields.map((field) => (
            <div key={field}>
              <Label className="capitalize">{field.replace(/_/g, ' ')}</Label>
              <Input
                type="number"
                value={editForm[field] || 0}
                onChange={(e) => setEditForm({ ...editForm, [field]: parseFloat(e.target.value) || 0 })}
              />
            </div>
          ))}
          
          <div className="col-span-2">
            <Label>Remarks</Label>
            <Input
              value={editForm.remarks || ''}
              onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })}
              placeholder="Optional remarks"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={onSave}>Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RejectBatchModal = ({
  isOpen,
  onClose,
  rejectReason,
  setRejectReason,
  onReject
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject Salary Batch</DialogTitle>
          <DialogDescription>
            Please provide a reason for rejection. The batch will be sent back to draft status.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <Label>Rejection Reason</Label>
          <Input
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Enter reason for rejection"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={onReject}>Reject Batch</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const BatchPayslipModal = ({
  isOpen,
  onClose,
  payslip,
  formatCurrency,
  onDownload,
  selectedBatch
}) => {
  if (!payslip) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Payslip - {payslip.employee_name}</DialogTitle>
          <DialogDescription>
            Pay Period: {payslip.month}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-4">
          {/* Employee Info */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="text-xs text-slate-500">Employee Code</p>
              <p className="font-medium">{payslip.employee_code}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Department</p>
              <p className="font-medium">{payslip.department || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Bank Account</p>
              <p className="font-medium">{payslip.bank_account_number || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">IFSC Code</p>
              <p className="font-medium">{payslip.ifsc_code || 'N/A'}</p>
            </div>
          </div>

          {/* Earnings & Deductions */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold text-slate-700 mb-2">Earnings</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span>Basic Pay</span><span>{formatCurrency(payslip.basic_pay)}</span></div>
                <div className="flex justify-between"><span>HRA</span><span>{formatCurrency(payslip.hra)}</span></div>
                <div className="flex justify-between"><span>DA</span><span>{formatCurrency(payslip.da)}</span></div>
                <div className="flex justify-between"><span>Special Allowance</span><span>{formatCurrency(payslip.special_allowance)}</span></div>
                <div className="flex justify-between"><span>Conveyance</span><span>{formatCurrency(payslip.conveyance)}</span></div>
                <div className="flex justify-between"><span>Medical</span><span>{formatCurrency(payslip.medical_allowance)}</span></div>
                <div className="flex justify-between"><span>Other Allowances</span><span>{formatCurrency(payslip.other_allowances)}</span></div>
                <div className="flex justify-between"><span>Overtime</span><span>{formatCurrency(payslip.overtime)}</span></div>
                <div className="flex justify-between"><span>Bonus</span><span>{formatCurrency(payslip.bonus)}</span></div>
                <div className="flex justify-between"><span>Arrears</span><span>{formatCurrency(payslip.arrears)}</span></div>
                <div className="flex justify-between font-bold border-t pt-1"><span>Gross Salary</span><span>{formatCurrency(payslip.gross_salary)}</span></div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-slate-700 mb-2">Deductions</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span>PF</span><span>{formatCurrency(payslip.pf_deduction)}</span></div>
                <div className="flex justify-between"><span>ESI</span><span>{formatCurrency(payslip.esi_deduction)}</span></div>
                <div className="flex justify-between"><span>TDS</span><span>{formatCurrency(payslip.tds_deduction)}</span></div>
                <div className="flex justify-between"><span>Professional Tax</span><span>{formatCurrency(payslip.professional_tax)}</span></div>
                <div className="flex justify-between"><span>Leave Deduction</span><span>{formatCurrency(payslip.leave_deduction)}</span></div>
                <div className="flex justify-between"><span>Loan Deduction</span><span>{formatCurrency(payslip.loan_deduction)}</span></div>
                <div className="flex justify-between"><span>Other Deductions</span><span>{formatCurrency(payslip.other_deductions)}</span></div>
                <div className="flex justify-between font-bold border-t pt-1 text-red-600"><span>Total Deductions</span><span>{formatCurrency(payslip.total_deductions)}</span></div>
              </div>
            </div>
          </div>

          {/* Net Salary */}
          <div className="p-4 bg-emerald-50 rounded-lg text-center">
            <p className="text-sm text-emerald-700">Net Payable</p>
            <p className="text-3xl font-bold text-emerald-800">{formatCurrency(payslip.net_salary)}</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {selectedBatch && (
            <Button onClick={() => onDownload(payslip)}>
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GenerateBatchModal;
